import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangleIcon, Building2Icon, ClockIcon, SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, StateComplianceData, US_STATES } from './types';
type CellStatus = 'not_started' | 'in_progress' | 'complete' | 'expired';
type StatusFilter = 'all' | 'complete' | 'in_progress' | 'expired';
interface LicenseMatrixProps {
  users: User[];
  licenses: Record<string, Record<string, LicenseData>>;
  michiganLicenses: Record<string, MichiganLicenseData>;
  stateCompliance: Record<string, StateComplianceData>;
  onCellClick: (userId: string, stateName: string) => void;
  onCompanyStateClick: (stateName: string) => void;
  onUserClick: (userId: string) => void;
}
const FILTER_OPTIONS: {
  key: StatusFilter;
  label: string;
  icon: string;
}[] = [{
  key: 'all',
  label: 'All',
  icon: ''
}, {
  key: 'complete',
  label: 'Complete',
  icon: '✅'
}, {
  key: 'in_progress',
  label: 'In Progress',
  icon: '🕐'
}, {
  key: 'expired',
  label: 'Expired',
  icon: '⚠️'
}];
const STATE_NAMES = US_STATES.map(s => s.name);
export function LicenseMatrix({
  users,
  licenses,
  michiganLicenses,
  stateCompliance,
  onCellClick,
  onCompanyStateClick,
  onUserClick
}: LicenseMatrixProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Pre-compute ALL statuses in one pass — single useMemo, no per-cell work
  const statusMap = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();
    const map: Record<string, Record<string, CellStatus>> = {};
    for (const user of users) {
      const userStatuses: Record<string, CellStatus> = {};
      for (const stateName of STATE_NAMES) {
        if (stateName === 'Michigan') {
          const mi = michiganLicenses[user.id];
          if (!mi) {
            userStatuses[stateName] = 'not_started';
          } else {
            const allComplete = mi.steps.every(Boolean);
            if (allComplete) {
              if (mi.expirationDate && new Date(mi.expirationDate).getTime() < todayTime && mi.renewals.length === 0) {
                userStatuses[stateName] = 'expired';
              } else {
                userStatuses[stateName] = 'complete';
              }
            } else if (mi.steps.some(Boolean)) {
              userStatuses[stateName] = 'in_progress';
            } else {
              userStatuses[stateName] = 'not_started';
            }
          }
        } else {
          const lic = licenses[user.id]?.[stateName];
          if (!lic) {
            userStatuses[stateName] = 'not_started';
          } else if (lic.status === 'complete' && lic.expirationDate && new Date(lic.expirationDate).getTime() < todayTime && lic.renewals.length === 0) {
            userStatuses[stateName] = 'expired';
          } else {
            userStatuses[stateName] = lic.status;
          }
        }
      }
      map[user.id] = userStatuses;
    }
    return map;
  }, [users, licenses, michiganLicenses]);
  // Filter users — uses pre-computed statusMap for O(1) lookups
  const filteredUsers = useMemo(() => {
    let result = users;
    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(u => u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      result = result.filter(u => {
        const userStatuses = statusMap[u.id];
        if (!userStatuses) return false;
        return STATE_NAMES.some(s => userStatuses[s] === statusFilter);
      });
    }
    return result;
  }, [users, search, statusFilter, statusMap]);
  const handleCellClick = useCallback((userId: string, stateName: string) => {
    onCellClick(userId, stateName);
  }, [onCellClick]);
  const handleUserClick = useCallback((userId: string) => {
    onUserClick(userId);
  }, [onUserClick]);
  return <div className="flex-1 min-h-0 flex flex-col bg-slate-900/30 border border-slate-800/80 rounded-xl shadow-2xl overflow-hidden" data-id="element-69" data-mp="itlrdzahjamb">
      {/* Header Actions */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-700/50" data-id="element-70" data-mp="dwvefktz8v54">
        <div className="flex flex-col gap-0.5 flex-shrink-0" data-id="element-71" data-mp="16jg5ek6jj585">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-72" data-mp="16jy921vj8lq3">
            Licensing & Compliance Grid
          </h2>
          <p className="text-[10px] text-slate-500" data-id="element-73" data-mp="wtmulle82u4y">Company requirements first · loan officers below</p>
        </div>

        <div className="flex items-center gap-1 bg-slate-800/40 border border-slate-700/40 rounded-lg p-0.5" data-id="element-74" data-mp="1u9ityqb8xt20">
          {FILTER_OPTIONS.map(opt => <button key={opt.key} onClick={() => setStatusFilter(opt.key)} className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${statusFilter === opt.key ? 'bg-slate-600/80 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-75" data-mp="1pgzhxxs7j5zb">
              {opt.icon && <span className="mr-1" data-id="element-76" data-mp="2bqprz85ixqk7">{opt.icon}</span>}
              {opt.label}
            </button>)}
        </div>

        <div className="flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30 w-56 flex-shrink-0" data-id="element-77" data-mp="2ft2ulnoz58cr">
          <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2 flex-shrink-0" data-id="element-78" data-mp="2pjfeddoysxhu" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Officers..." className="w-full bg-transparent text-xs text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-79" data-mp="2uhgx1xihauss" />
        </div>
      </div>

      {/* Matrix Grid */}
      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar bg-slate-900/50 relative" data-id="element-80" data-mp="14b18kir8pw3r">
        <table className="w-full border-collapse text-sm text-left" data-id="element-81" data-mp="zby0sqjvlbs3">
          <thead className="sticky top-0 z-30 bg-slate-800 text-slate-400 text-xs uppercase tracking-wider" data-id="element-82" data-mp="1d68vdoik0062">
            <tr data-id="element-83" data-mp="18d5zh9qb0emp">
              <th className="sticky left-0 z-40 bg-slate-800 border-b border-r border-slate-700/50 px-4 py-3 min-w-[180px] max-w-[180px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-84" data-mp="18cmefk5cxa4g">
                Officer
              </th>
              <th className="sticky left-[180px] z-40 bg-slate-800 border-b border-r border-slate-700/50 px-4 py-3 min-w-[100px] max-w-[100px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-85" data-mp="1w4bbfdt4imrq">
                NMLS ID
              </th>
              {US_STATES.map(state => <th key={state.abbr} className="border-b border-r border-slate-700/50 px-2 py-3 text-center min-w-[44px] max-w-[44px] font-semibold" title={state.name} data-id="element-86" data-mp="1qk44ky9oezg">
                  {state.abbr}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50" data-id="element-87" data-mp="3a0uuhcoa68xa">
            <tr className="bg-emerald-500/[0.07]" data-id="element-88" data-mp="39y8o3gd4lzgn">
              <td className="sticky left-0 z-20 border-r border-emerald-500/20 bg-[#10231f] px-4 py-2.5 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-89" data-mp="3eymjnyxjjgxs">
                <div className="flex items-center gap-2 text-emerald-100" data-id="element-90" data-mp="3049nwmwm4prc">
                  <Building2Icon className="h-4 w-4 flex-shrink-0 text-emerald-400" data-id="element-91" data-mp="3521ixpsk0rzr" />
                  <div className="min-w-0" data-id="element-92" data-mp="2r2ijdldt6oz2">
                    <span className="block truncate text-sm font-bold" data-id="element-93" data-mp="2r3tmrk3cdupn">Olympian Mortgage</span>
                    <span className="block text-[10px] font-medium uppercase tracking-wider text-emerald-400/80" data-id="element-94" data-mp="2w2o74qgyjrbv">Company compliance</span>
                  </div>
                </div>
              </td>
              <td className="sticky left-[180px] z-20 border-r border-emerald-500/20 bg-[#10231f] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400/80 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-95" data-mp="u747wyc10paa">
                All states
              </td>
              {US_STATES.map(state => {
              const compliance = stateCompliance[state.name];
              const hasCompliance = Boolean(compliance?.renewalDate || compliance?.suretyBondAmount || compliance?.suretyBondHeldBy || compliance?.secretaryOfStateRenewalDate || compliance?.auditDate);
              return <td key={state.abbr} className="border-r border-emerald-500/15 p-0 text-center align-middle" data-id="element-96" data-mp="163mzwxueyia8">
                    <button onClick={() => onCompanyStateClick(state.name)} className="flex min-h-[52px] w-full items-center justify-center transition-colors hover:bg-emerald-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400" aria-label={`Open Olympian Mortgage compliance for ${state.name}`} title={`${state.name} company compliance`} data-id="element-97" data-mp="34jhvxh0y9r5q">
                      {hasCompliance ? <ShieldCheckIcon className="h-4 w-4 text-emerald-400" aria-hidden="true" data-id="element-98" data-mp="1008oczppk3f9" /> : <span className="h-2 w-2 rounded-full border border-slate-600" aria-hidden="true" data-id="element-99" data-mp="3d3xip16sbme" />}
                    </button>
                  </td>;
            })}
            </tr>
            {filteredUsers.length === 0 ? <tr data-id="element-100" data-mp="hbu02k6rz3i5">
                <td colSpan={US_STATES.length + 2} className="px-4 py-8 text-center text-slate-500" data-id="element-101" data-mp="h865dhplgb1u">
                  No loan officers found.
                </td>
              </tr> : filteredUsers.map(user => {
            const userStatuses = statusMap[user.id];
            return <tr key={user.id} className="group hover:bg-slate-800/40" data-id="element-102" data-mp="tx6oy2pbxxrn">
                    <td className="sticky left-0 z-20 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-700/50 px-4 py-2.5 font-medium text-slate-200 truncate shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-103" data-mp="yr2p9bt2fwpz">
                      <button onClick={() => handleUserClick(user.id)} className="hover:text-emerald-400 text-left w-full truncate" data-id="element-104" data-mp="62x08cuzblkc">
                        {user.name}
                      </button>
                    </td>
                    <td className="sticky left-[180px] z-20 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-700/50 px-4 py-2.5 text-slate-400 tabular-nums shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-105" data-mp="k2etb4fb5kzi">
                      {user.nmlsId}
                    </td>
                    {US_STATES.map(state => {
                const status = userStatuses?.[state.name] || 'not_started';
                return <td key={state.abbr} onClick={() => handleCellClick(user.id, state.name)} className="border-r border-slate-700/50 p-0 cursor-pointer hover:bg-slate-700/50 text-center align-middle" data-id="element-106" data-mp="375rae2spaabx">
                          <div className="flex items-center justify-center h-full w-full min-h-[40px]" data-id="element-107" data-mp="2s8qgbsn1e3aa">
                            {status === 'complete' && <span className="text-base leading-none" role="img" aria-label="Complete" data-id="element-108" data-mp="2o757cidmi95d">
                                ✅
                              </span>}
                            {status === 'in_progress' && <ClockIcon className="w-4 h-4 text-amber-400" strokeWidth={2.5} data-id="element-109" data-mp="10jr37igyhtfy" />}
                            {status === 'expired' && <AlertTriangleIcon className="w-4 h-4 text-red-400" strokeWidth={2.5} data-id="element-110" data-mp="wki7i82cvlhr" />}
                          </div>
                        </td>;
              })}
                  </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}